import { AxiosInstance } from 'axios';
import {
  IQuoteFreightTemplateData,
  IQuotePaymentTermTemplateData,
  IQuoteServiceTemplateData,
  IQuoteTemplateData,
  IQuoteTermTemplateData
} from '@/models';
import {
  IQuoteTemplateFilterParams,
  IQuoteTemplateService
} from './quotetemplate.types';

export abstract class AbstractQuoteTemplateService<T extends IQuoteTemplateData>
  implements IQuoteTemplateService<T> {
  constructor(protected axios: AxiosInstance, protected endpoint: string) {}

  fetchQuoteTemplates(params?: IQuoteTemplateFilterParams): Promise<T[]> {
    return this.axios
      .get<T[]>(`${this.endpoint}`, { params })
      .then(({ data }) => data);
  }

  fetchQuoteTemplate(id: number): Promise<T> {
    return this.axios.get<T>(`${this.endpoint}${id}/`).then(({ data }) => data);
  }

  createQuoteTemplate(data: T): Promise<T> {
    return this.axios
      .post<T>(`${this.endpoint}`, data)
      .then(({ data }) => data);
  }

  updateQuoteTemplate(data: T): Promise<T> {
    return this.axios
      .patch<T>(`${this.endpoint}${data.id}/`, data)
      .then(({ data }) => data);
  }

  deleteQuoteTemplate(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}${id}/`).then(({ data }) => data);
  }

  // TRANSLATIONS
  fetchQuoteTemplateTranslations(templateId: number): Promise<T[]> {
    return this.axios
      .get<T[]>(`${this.endpoint}${templateId}/translations/`)
      .then(({ data }) => data);
  }

  fetchQuoteTemplateTranslation(
    templateId: number,
    language: string
  ): Promise<T> {
    return this.axios
      .get<T>(`${this.endpoint}${templateId}/translations/${language}/`)
      .then(({ data }) => data);
  }

  createQuoteTemplateTranslation(templateId: number, data: T): Promise<T> {
    return this.axios
      .post<T>(`${this.endpoint}${templateId}/translations/`, data)
      .then(({ data }) => data);
  }

  updateQuoteTemplateTranslation(templateId: number, data: T): Promise<T> {
    return this.axios
      .put<T>(
        `${this.endpoint}${templateId}/translations/${data.language}/`,
        data
      )
      .then(({ data }) => data);
  }

  deleteQuoteTemplateTranslation(
    templateId: number,
    language: string
  ): Promise<any> {
    return this.axios
      .delete(`${this.endpoint}${templateId}/translations/${language}/`)
      .then(({ data }) => data);
  }
}

export class QuoteServiceTemplateService extends AbstractQuoteTemplateService<
  IQuoteServiceTemplateData
> {
  constructor(axios: AxiosInstance) {
    super(axios, 'v1/quotes/templates/services/');
  }
}

export class QuoteFreightTemplateService extends AbstractQuoteTemplateService<
  IQuoteFreightTemplateData
> {
  constructor(axios: AxiosInstance) {
    super(axios, 'v1/quotes/templates/freights/');
  }
}

export class QuotePaymentTermTemplateService extends AbstractQuoteTemplateService<
  IQuotePaymentTermTemplateData
> {
  constructor(axios: AxiosInstance) {
    super(axios, 'v1/quotes/templates/payment-terms/');
  }
}

export class QuoteTermTemplateService extends AbstractQuoteTemplateService<
  IQuoteTermTemplateData
> {
  constructor(axios: AxiosInstance) {
    super(axios, 'v1/quotes/templates/terms/');
  }

  setQuoteTemplateOrder(
    templateId: number,
    order: number
  ): Promise<IQuoteTermTemplateData> {
    return this.axios
      .put<IQuoteTermTemplateData>(`${this.endpoint}${templateId}/set-order/`, {
        order: order
      })
      .then(({ data }) => data);
  }
}
